import {
    AdjustCol,
    CentsCol,
    RenderStringCol, RenderStringCol2,
    StringCol,
    Table,
    ViewOnly
} from "../../../../misc/scroller/Table";
import {useUser} from "../../../../misc/Permission";
import {api} from "../../../../api/API";
import React, {useContext, useState} from "react";
import {useProjectId} from "../ProjectName";
import {priceTableName} from "./Pricing";
import {getAll} from "../../../logistics/routes/useGetAll";
import {hollowMetalDoor, hollowMetalFrame, woodDoor} from "../../quote/openings/Const";
import {ChangePreviewContext} from "../ShopDrawingChangePreview";
import {calcMargin} from "../../quote/pricing/QuoteMiscPricing";
import {groupTitle} from "../../quote/pricing/BeforeGroup";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {groupByArr} from "nate-react-api-helpers";
import {Div10TypeToName} from "../div10/Div10Tabs";

export type PriceSummaryLine = {
    id: number;
    name: string;
    extendedCost: number;
    qty: number;
    markup: number;
    margin: string;
    extendedPrice: number;

    indent: number;
    canExpand: boolean;
    expandPath: string[]
    expanded: boolean;
    total: boolean;
}


export function Summary(props: {
    sov?: number;
    awarded?: boolean;
}) {
    const project = useProjectId();
    const u = useUser();
    const previewContext = useContext(ChangePreviewContext);
    const previewChangeset = previewContext.enabled ? previewContext.sessionId : undefined;
    const [selectedPath, setSelectedPath] = useState<string[]>([]);

    if(u === null) return null;

    return (
        <Table<PriceSummaryLine>
            name={priceTableName(project, "summary")}
            globalPrefsName={priceTableName(0, "summary")}
            cellCustomize={{
                styles: row => {
                    if(row.total) return {
                        fontWeight: "bold",
                    };
                }
            }}
            fetch={async (ctx) => {

                const listP = getAll(ctx, offset =>
                    api.pricing.list({
                        project: project, offset, previewChangeset,
                        awarded: props.awarded,
                    }))

                const miscP = getAll(ctx, offset =>
                    api.miscPricing.list({
                        project: project,
                        offset,
                        previewChangeset,
                        awarded: props.awarded,
                    }), {useLazy: true})

                const ridersP = getAll(ctx, offset => api.pricing.listRiders({
                    project,
                    offset,
                    awarded: props.awarded,
                }), {useLazy: true})

                const list = await listP;
                const misc = await miscP;
                const riders = await ridersP;

                let groups: PriceSummaryLine[] = [];

                const shouldShow = (path: string[]) => {
                    if(path.length === 1) return true;
                    if(arrayHasPrefix(path, selectedPath) && path.length <= selectedPath.length + 1) return true
                    if(path.length <= selectedPath.length && arrayHasPrefix(selectedPath, path.slice(0, path.length-1))) return true;

                    return false;
                }

                const isExpanded = (path: string[]) => {
                    console.log("isExpanded", selectedPath, path, arrayHasPrefix(path, selectedPath));
                    return arrayHasPrefix(path, selectedPath) && path.length === selectedPath.length;
                }

                const checkParent = (path: string[]) => {
                    const g = groups.find(g => arrayEq(g.expandPath, path));
                    if(g) return g;

                    const g2: PriceSummaryLine = {
                        id: groups.length+1,
                        name: path[path.length-1],
                        extendedCost: 0,
                        qty: 0,
                        markup: 0,
                        margin: "",
                        extendedPrice: 0,
                        indent: path.length-1,
                        canExpand: true,
                        expandPath: path,
                        expanded: isExpanded(path),
                        total: false,
                    }

                    groups.push(g2);
                    return g2;
                }

                let totalPriceCents = 0;
                let totalCostCents = 0;

                const add = (path: string[], list: {
                    extendedPrice: number;
                    qty: number;
                    unitCostCents: number;
                }[]) => {
                    const extPrice = list.reduce((acc, l) => acc + l.extendedPrice, 0);
                    let qty = list.reduce((acc, l) => acc + l.qty, 0);
                    const extCost = list.reduce((acc, l) => acc + l.unitCostCents * l.qty, 0);

                    totalPriceCents += extPrice;
                    totalCostCents += extCost;

                    for(let i = 0; i < path.length - 1; i++) {
                        const subPath = path.slice(0, i+1);

                        if(!shouldShow(subPath)) {
                            continue
                        }

                        const p = checkParent(subPath);
                        p.extendedPrice += extPrice;
                        p.extendedCost += extCost;
                        p.qty += qty;

                        p.markup = makeMarkup(p.extendedCost, p.extendedPrice);
                        p.margin = calcMargin(p.extendedCost, p.extendedPrice);
                    }

                    if(!shouldShow(path)) {
                        return;
                    }

                    groups.push({
                        id: groups.length+1,
                        name: path[path.length-1],
                        extendedCost: extCost,
                        qty: qty,
                        markup: makeMarkup(extCost, extPrice),
                        margin: calcMargin(extCost, extPrice),
                        extendedPrice: extPrice,

                        canExpand: false,
                        expandPath: path,
                        expanded: true,
                        indent: path.length-1,
                        total: false,
                    });
                }

                add(["Hollow Metal", "Frames & Anchors", "Frames"], list.filter(l => l.productType === "frame" && l.frame?.series === hollowMetalFrame));
                add(["Hollow Metal", "Frames & Anchors", "Anchors"], list.filter(l => l.productType === "frame-anchor"));
                add(["Hollow Metal", "Doors"], list.filter(l => l.productType === "door" && l.door?.series === hollowMetalDoor));
                add(["Wood Doors"], list.filter(l => l.productType === "door" && l.door?.series === woodDoor));
                add(["Finishing Hardware"], list.filter(l => l.productType === "hardware"));
                add(["Glazing"], list.filter(l => l.productType === "glazing"));

                const div10Categories = groupByArr(list.filter(l => l.productType === "div-10"), v => v.category);
                if(div10Categories.length > 0) {
                    div10Categories.forEach(d => {
                        add(["Div 10", Div10TypeToName(d.key as any)], d.values);
                    });
                } else {
                    add(["Div 10"], list.filter(l => l.productType === "div-10"));
                }

                riders.forEach(r => {
                    add(["Riders", r.name], [{
                        ...r,
                    }]);
                })

                misc.forEach(m => {
                    add(["Misc", m.name], [{
                        ...m,
                        qty: 1,
                    }]);
                })

                if(props.awarded) {
                    groups = groups.filter(g => g.qty > 0 || g.total);
                }

                groups.push({
                    id: groups.length+1,
                    name: "Total",
                    extendedCost: totalCostCents,
                    qty: 1,
                    markup: makeMarkup(totalCostCents, totalPriceCents),
                    margin: calcMargin(totalCostCents, totalPriceCents),
                    extendedPrice: totalPriceCents,
                    indent: 0,
                    canExpand: false,
                    expandPath: [],
                    expanded: true,
                    total: true,
                })

                return groups;
            }}
            columns={[
                RenderStringCol2<PriceSummaryLine>("Name",
                    d => d.name,
                    d => {
                        return (<div style={{paddingLeft: d.indent * 20, display: "flex"}}>
                            <div style={{
                                flex: 1,
                            }}
                                 className={d.total ? groupTitle : undefined}
                            >{d.name}</div>
                            {d.canExpand && <div>
                                <button style={{background: "transparent", border: "none", padding: 0, height: "1em", fontSize: 12, cursor: "pointer"}} onClick={() => {
                                    if(!d.expanded) {
                                        setSelectedPath(d.expandPath)
                                    } else {
                                        setSelectedPath(d.expandPath.slice(0, d.expandPath.length - 1))
                                    }
                                }}>{d.expanded ? <KeyboardArrowUpIcon fontSize="inherit" />: <KeyboardArrowDownIcon fontSize="inherit" />}</button>
                            </div>}
                        </div>)
                    }, 300
                ),
                ViewOnly(CentsCol("Total Cost", "extendedCost", 80)),
                ViewOnly(CentsCol("Proj. Markup", "markup", 80)),
                ViewOnly(AdjustCol(StringCol("Proj. Margin", "margin", 80), {
                    alignRight: true,
                })),
                ViewOnly(CentsCol("Total Price", "extendedPrice", 100)),
                RenderStringCol("-", () => ""),
            ]}
            fetchDeps={[project, props.sov, previewChangeset, selectedPath]}
        />
    )
}

function makeMarkup(extCost: number, extPrice: number) {
    return extPrice - extCost;
}

function arrayEq(a: string[], b: string[]) {
    if(a.length !== b.length) return false;

    for(let i = 0; i < a.length; i++) {
        if(a[i] !== b[i]) return false;
    }

    return true;
}

function arrayHasPrefix(base: string[], prefix: string[]) {
    if(base.length === 0) return false;

    for(let i = 0; i < prefix.length && i < base.length; i++) {
        if(prefix[i] !== base[i]) return false;
    }

    return true;
}